import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api.js';
import { useEntityScope } from './useEntityScope.js';

export function useRecurrences(filters = {}) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  const qs = params.toString() ? `?${params.toString()}` : '';
  return useQuery({
    queryKey: ['recurrences', filters],
    queryFn: () => api.get(`/recurrences${qs}`),
    staleTime: 30_000,
  });
}

export function useRecurrenceTransactions(id) {
  return useQuery({
    queryKey: ['recurrences', id, 'transactions'],
    queryFn: () => api.get(`/recurrences/${id}/transactions`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function usePendingRecurrencesCount() {
  const { isGroup } = useEntityScope();
  return useQuery({
    queryKey: ['recurrences', 'pending', 'count'],
    queryFn: () => api.get('/recurrences?status=pending'),
    staleTime: 30_000,
    enabled: !isGroup,
    select: (data) => {
      if (Array.isArray(data)) return data.length;
      return data?.total ?? data?.pagination?.total ?? 0;
    },
  });
}

export function useCreateRecurrence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/recurrences', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recurrences'] });
    },
  });
}

export function usePatchRecurrence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) =>
      api.patch(`/recurrences/${id}`, body),
    // Optimistic update : on merge tout ce qui a été passé (status OU
    // category_id OU frequency OU end_date), sans écraser les autres
    // champs. Ancien code écrasait status à undefined quand on PATCH
    // uniquement un autre champ.
    onMutate: async (vars) => {
      const { id, ...patch } = vars;
      await qc.cancelQueries({ queryKey: ['recurrences'] });
      const prev = qc.getQueriesData({ queryKey: ['recurrences'] });
      qc.setQueriesData({ queryKey: ['recurrences'] }, (old) => {
        if (!old) return old;
        const items = Array.isArray(old) ? old : old.data ?? old.items ?? old;
        if (!Array.isArray(items)) return old;
        return items.map((r) => (r.id === id ? { ...r, ...patch } : r));
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        ctx.prev.forEach(([key, value]) => qc.setQueryData(key, value));
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['recurrences'] });
    },
  });
}
